import { consola } from 'consola'
import { ref, computed } from 'vue';
import { useInstitutionStore } from '@entities/institution';
import { useUserProfileStore } from '@entities/user';
import { useModalStore } from '@shared/model';
import type { TelegramAuthStatusResponse } from '@entities/auth';
import { useSessionManager } from '~/entities/auth';
import { useLogoutHandler } from '~/features/auth/model';
import { TelegramAuthService } from '~/features/auth/lib/TelegramAuthService';
import { useAuthAnalytics } from '~/features/auth/model/useAuthAnalytics';

/**
 * Composable для авторизации через Telegram (правильная архитектура OAuth 2.0)
 * 
 * ✅ Поддержка веб и мобильных платформ (Capacitor)
 * ✅ Использует TelegramAuthService для бизнес-логики (SOLID)
 * ✅ Автоматическое определение платформы
 * ✅ Стандартный OAuth 2.0 flow (как Yandex ID)
 * 
 * Принцип работы:
 * 1. Пользователь нажимает кнопку "Войти через Telegram"
 * 2. Отправляем POST /auth/telegram/initiate на бэкенд
 * 3. Получаем telegramLink и открываем:
 *    - Мобильные (iOS/Android): Capacitor Browser (in-app browser)
 *    - Веб: window.open (новая вкладка, не затирает текущую!)
 * 4. Начинаем polling GET /auth/telegram/status/{sessionId} каждые 5 секунд
 * 5. Когда пользователь авторизуется в Telegram боте, получаем токены
 * 6. Закрываем Capacitor Browser на мобильных
 * 
 * @example
 * const { initTelegramAuth, isLoading, errorMessage } = useTelegramAuth()
 * await initTelegramAuth()
 */
export const useTelegramAuth = () => {
  const storeInstitution = useInstitutionStore();
  const sessionManager = useSessionManager();
  const userProfileStore = useUserProfileStore();
  const logoutHandler = useLogoutHandler();
  const modalStore = useModalStore();
  const authAnalytics = useAuthAnalytics();

  const isLoading = ref(false);
  const errorMessage = ref<string | null>(null);
  const telegramSessionId = ref<string | null>(null);
  const telegramLink = ref<string | null>(null);
  const showTelegramLinkModal = ref(false);
  
  // Polling управляется через TelegramAuthService
  let pollingController: ReturnType<typeof TelegramAuthService.createPolling> | null = null;

  // Контроль логов
  const ENABLE_LOGS = true;
  const log = (...args: any[]) => ENABLE_LOGS && consola.debug('[TelegramAuth]', ...args);
  const warn = (...args: any[]) => ENABLE_LOGS && consola.warn('[TelegramAuth]', ...args);
  const error = (...args: any[]) => ENABLE_LOGS && consola.error('[TelegramAuth]', ...args);

  // Проверка доступности Telegram авторизации
  const isTelegramAuthEnabled = computed(() => {
    return TelegramAuthService.isTelegramAuthEnabled(storeInstitution.institution);
  });

  /**
   * Останавливает опрос статуса авторизации
   */
  const stopPolling = () => {
    if (pollingController) {
      pollingController.stop();
      pollingController = null;
      log('Опрос статуса остановлен');
    }
  };

  /**
   * Обрабатывает изменение статуса авторизации
   */
  const handleStatusChange = async (status: TelegramAuthStatusResponse) => {
    // Детальное логирование для отладки
    log('Статус авторизации (JSON):', JSON.stringify(status, null, 2));
    log('status.status:', status.status);
    log('status.accessToken:', status.accessToken ? 'ЕСТЬ' : 'НЕТ');
    log('status.user:', status.user ? 'ЕСТЬ' : 'НЕТ');

    // Если есть accessToken и user - значит авторизация завершена успешно
    // Бэкенд может не возвращать поле status, поэтому проверяем наличие токенов
    const isCompleted = status.accessToken && status.user;
    const isExpired = status.status === 'expired';
    const isFailed = status.status === 'failed';

    if (isCompleted) {
      log('✅ Авторизация через Telegram успешна!');
      authAnalytics.trackCallback('telegram', true);

      // Закрываем Capacitor Browser на мобильных
      await TelegramAuthService.closeTelegramBrowser();

      // ✅ КРИТИЧНО: Ждём сохранения токенов
      await sessionManager.setSession(
        status.accessToken,
        status.sessionId || telegramSessionId.value!,
        status.refreshToken,
      );
      logoutHandler.handleSuccessfulLogin();

      await userProfileStore.fetchUser();

      // ✅ Применение реферального токена (если есть)
      if (process.client) {
        try {
          const { useApplyReferralToken } = await import('~/features/profile/@x/auth');
          await useApplyReferralToken();
        } catch (error_) {
          warn('Ошибка применения реферального токена (не критично):', error_);
        }
      }

      if (process.client) {
        try {
          log('Инициализация push notifications после входа через Telegram...');
          const pushSettingsModule = await import('~/features/notifications/@x/auth');
          const pushSettings = pushSettingsModule.usePushNotificationSettings();
          await pushSettings.initPushNotificationsAfterAuth();
          log('Push notifications инициализированы');
        } catch (error_) {
          warn('Ошибка инициализации push notifications:', error_);
        }
      }

      modalStore.close();
      log('Модальное окно авторизации закрыто');

      isLoading.value = false;
      telegramSessionId.value = null;
      
      // Останавливаем polling
      stopPolling();
    } else if (isExpired) {
      // Истечение и отказ различаем: первое — «ушёл и не вернулся», второе — сервис
      // ответил отказом. Лечатся они по-разному.
      authAnalytics.trackCallback('telegram', false, 'expired');
      error('Токен авторизации истёк');
      errorMessage.value = 'Время авторизации истекло. Попробуйте ещё раз.';
      isLoading.value = false;
      telegramSessionId.value = null;
      stopPolling();
    } else if (isFailed) {
      authAnalytics.trackCallback('telegram', false, status.status || 'failed');
      error('Авторизация не удалась');
      errorMessage.value = status.message || 'Не удалось авторизоваться через Telegram';
      isLoading.value = false;
      telegramSessionId.value = null;
      stopPolling();
    } else {
      log('Продолжаем ожидание авторизации... Текущий статус:', status.status || 'pending');
    }
  };

  /**
   * Обрабатывает ошибки polling
   */
  const handlePollingError = (err: any) => {
    error('Ошибка проверки статуса:', err);
    errorMessage.value = err?.message || 'Произошла ошибка при проверке статуса авторизации';
    isLoading.value = false;
  };

  /**
   * Начинает опрос статуса авторизации
   */
  const startPolling = (sessionId: string) => {
    log('Начат опрос статуса авторизации (каждые 5 секунд, максимум 5 минут)');

    pollingController = TelegramAuthService.createPolling(
      sessionId,
      handleStatusChange,
      handlePollingError,
    );

    pollingController.start();
  };

  /**
   * Открывает Telegram по ссылке (вызывается из кнопки в UI)
   */
  const openTelegramLink = async () => {
    if (!telegramLink.value) {
      warn('Ссылка на Telegram не найдена');
      return;
    }

    log('Открытие Telegram по ссылке:', telegramLink.value);

    const result = await TelegramAuthService.openTelegramUrl(telegramLink.value);

    if (result.success) {
      log(`✅ Telegram открыт через ${result.method}`);
      showTelegramLinkModal.value = false;
    } else {
      warn('⚠️ Не удалось открыть Telegram');
    }
  };

  /**
   * Инициирует процесс авторизации через Telegram
   */
  const initTelegramAuth = async () => {
    log('Инициализация Telegram авторизации');

    if (!isTelegramAuthEnabled.value) {
      warn('Telegram авторизация не включена для этого заведения');
      errorMessage.value = 'Telegram авторизация недоступна';
      return;
    }

    if (typeof window === 'undefined') {
      warn('Window не определён (SSR)');
      return;
    }

    try {
      isLoading.value = true;
      errorMessage.value = null;

      const response = await TelegramAuthService.initiateAuth({
        institutionId: String(storeInstitution.institution?.id),
        userAgent: navigator.userAgent,
        clientFingerprint: await TelegramAuthService.generateClientFingerprint(),
      });

      log('✅ Токен успешно сгенерирован:', response);

      telegramSessionId.value = response.sessionId;
      telegramLink.value = response.telegramLink;

      startPolling(response.sessionId);

      const openResult = await TelegramAuthService.openTelegramUrl(response.telegramLink);

      if (openResult.success) {
        log(`✅ Telegram открыт через ${openResult.method}`);
        // С этого момента пользователь вне приложения: следующий шаг воронки увидим
        // только когда поллинг принесёт исход.
        authAnalytics.trackRedirectStart('telegram');
        // Продолжаем опрос статуса авторизации
      } else {
        log('⚠️ Не удалось открыть Telegram автоматически, показываем модальное окно');
        showTelegramLinkModal.value = true;
        isLoading.value = false;
      }

    } catch (error_: any) {
      error('Ошибка при инициализации Telegram авторизации:', error_);
      errorMessage.value = error_?.data?.message || 'Произошла ошибка при авторизации через Telegram';
      isLoading.value = false;
    }
  };

  /**
   * Закрывает модальное окно с кнопкой Telegram
   */
  const closeTelegramLinkModal = () => {
    showTelegramLinkModal.value = false;
    // Останавливаем опрос если пользователь закрыл окно
    if (pollingController?.isActive()) {
      stopPolling();
      isLoading.value = false;
      telegramSessionId.value = null;
      telegramLink.value = null;
    }
  };

  /**
   * Очистка состояния при размонтировании компонента
   */
  const cleanup = () => {
    stopPolling();
    isLoading.value = false;
    errorMessage.value = null;
    telegramSessionId.value = null;
    telegramLink.value = null;
    showTelegramLinkModal.value = false;
  };

  return {
    initTelegramAuth,
    openTelegramLink,
    closeTelegramLinkModal,
    cleanup,
    isLoading,
    errorMessage,
    isTelegramAuthEnabled,
    showTelegramLinkModal,
    telegramLink,
  };
};



